import React, { useRef, useState, useEffect } from 'react'
import { reactMount } from '@/lib/reactMount'
import { Button } from '@/components/Button'
import { BrandLogo } from '@/components/Logos'
import { ArrowRightCircle, Search, Chevron, Delete, Menu, ArrowRight } from '@/components/Icons'
import { gtmTrackEvent } from '@/lib/analytics'
import { getImageByBreakpoint } from '@/lib/media'

import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel
} from '@headlessui/react'

import { ChevronDownIcon } from '@heroicons/react/20/solid'

// Custom hook for popover hover functionality
function usePopoverHover (buttonRef, isOpenRef, openPopover, closePopover, enabled = true, isProgrammaticClickRef = null) {
  const openTimeoutRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const isHoveringRef = useRef(false)

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (openTimeoutRef.current) {
        clearTimeout(openTimeoutRef.current)
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  // Note: We don't use useEffect to watch isOpenRef.current because refs don't trigger re-renders
  // The ref is updated in the render prop and checked directly in the handlers

  const handleMouseEnter = () => {
    if (!enabled) return

    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }

    // If already open, do nothing
    if (isOpenRef.current) {
      isHoveringRef.current = true
      return
    }

    isHoveringRef.current = true

    // Schedule open after delay
    openTimeoutRef.current = setTimeout(() => {
      if (isHoveringRef.current && buttonRef.current) {
        // Mark as programmatic click before programmatically clicking the button
        if (isProgrammaticClickRef) {
          isProgrammaticClickRef.current = true
        }
        // Programmatically click the button to open popover
        buttonRef.current.click()
        // Reset flag after a short delay to allow onClick handler to check it
        setTimeout(() => {
          if (isProgrammaticClickRef) {
            isProgrammaticClickRef.current = false
          }
        }, 0)
        openTimeoutRef.current = null
      }
    }, 150) // 150ms delay before opening
  }

  const handleMouseLeave = () => {
    if (!enabled) return

    isHoveringRef.current = false

    // Clear any pending open timeout
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }

    // If not open, do nothing
    if (!isOpenRef.current) return

    // Schedule close after delay
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current && closePopover) {
        closePopover()
        closeTimeoutRef.current = null
      }
    }, 200) // 200ms delay before closing
  }

  const handlePanelMouseEnter = () => {
    if (!enabled) return

    // Cancel any pending close when mouse enters panel
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    isHoveringRef.current = true
  }

  const handlePanelMouseLeave = () => {
    if (!enabled) return

    isHoveringRef.current = false

    // Schedule close after delay
    if (isOpenRef.current) {
      closeTimeoutRef.current = setTimeout(() => {
        if (!isHoveringRef.current && closePopover) {
          closePopover()
          closeTimeoutRef.current = null
        }
      }, 200)
    }
  }

  return {
    handleMouseEnter,
    handleMouseLeave,
    handlePanelMouseEnter,
    handlePanelMouseLeave
  }
}

// Wrapper component for Popover with hover functionality
function HoverablePopover ({ navSection, idx, isOpen, onOpenChange, isDesktop, buttonRef }) {
  // Use refs to store the current open/close functions from render prop
  const openStateRef = useRef(false)
  const closeFunctionRef = useRef(null)
  // Ref to track if click is programmatic (from hover) vs user click
  const isProgrammaticClickRef = useRef(false)

  // Call hook at component level, passing ref for open state
  const hoverHandlers = usePopoverHover(
    buttonRef,
    openStateRef,
    null,
    () => closeFunctionRef.current?.(),
    isDesktop,
    isProgrammaticClickRef
  )

  // Use effect to sync state when popover opens/closes
  useEffect(() => {
    // This effect helps clear timeouts when popover state changes externally
    // The actual state is tracked via ref in the render prop
  }, [isOpen])

  return (
    <Popover>
      {({ open, close }) => {
        // Update refs with current values
        openStateRef.current = open
        closeFunctionRef.current = close

        if (open !== isOpen) setTimeout(() => onOpenChange(idx, open), 0)

        const { handleMouseEnter, handleMouseLeave, handlePanelMouseEnter, handlePanelMouseLeave } = hoverHandlers

        return (
          <>
            <PopoverButton
              as={navSection.Link
                ? 'a'
                : 'button'}
              {...(navSection.Link
                ? {
                    href: navSection.Link.Url,
                    target: navSection.Link.Target || undefined,
                    rel: navSection.Link.Target === '_blank' ? 'noreferrer' : undefined
                  }
                : {})}
              ref={buttonRef}
              className={`pl-3 pr-1 py-[9px] outline-none flex items-center gap-x-1 text-sm font-semibold rounded-full ${navSection.Link ? 'cursor-pointer' : 'cursor-default'} ${open ? 'bg-primary-100' : ''}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                // If there's a link and it's a user click (not programmatic from hover)
                if (navSection.Link?.Url && !isProgrammaticClickRef.current) {
                  // Prevent popover toggle behavior
                  e.stopPropagation()
                  e.preventDefault()
                  // Close the popover if it's open
                  if (open) {
                    close()
                  }
                  // Navigate to the link (PopoverButton prevents default, so we need to navigate manually)
                  // If no link or programmatic click, let the default PopoverButton behavior happen (toggle popover)
                  if (navSection.Link.Target === '_blank') {
                    window.open(navSection.Link.Url, '_blank', 'noopener,noreferrer')
                  } else {
                    window.location.href = navSection.Link.Url
                  }
                }
              }}
            >
              {navSection.Title}
              {/* {navSection.Link} */}
              <Chevron className={`flex-none translate-y-0.5 ${open ? 'rotate-180' : ''}`} />
            </PopoverButton>
            <PopoverPanel
              transition
              className='absolute p-4 max-w-[calc(100vw-32px)] left-[50%] -translate-x-[50%] top-[80px] bg-default-50 rounded-[20px] shadow-md transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
              onMouseEnter={handlePanelMouseEnter}
              onMouseLeave={handlePanelMouseLeave}
            >
              <div className='flex flex-start'>
                {navSection.SubNavigationGroup.map((group, gidx) => (
                  <React.Fragment key={gidx}>
                    {renderSubNavigationGroup(group)}
                  </React.Fragment>
                ))}
              </div>
            </PopoverPanel>
          </>
        )
      }}
    </Popover>
  )
}

// Helper renderers for sub-navigation types
function renderTitleSummaryColumn (title, column) {
  return column && column.length > 0
    ? (
      <>
        {title && <h3 className='uppercase text-[10px] tracking-[1.6px] text-default-500 custom-md:text-default-400 custom-md:pt-1 pb-1 custom-md:pb-4'>{title}</h3>}
        {column.map((item, idx) => (
          <a
            key={idx}
            href={item.Link?.Url || '#'}
            target={item.Link?.Target || undefined}
            rel={item.Link?.Target === '_blank' ? 'noreferrer' : undefined}
            className='group flex justify-between items-start custom-md:items-center gap-6 relative rounded-lg py-1 custom-md:py-2 text-sm/6 hover:bg-default-100 max-custom-md:hover:bg-transparent p-1 mb-2 custom-md:mb-4 last:mb-1'
          >
            <div>
              <p className='text-base custom-md:text-xl custom-md:pb-1 text-default-900 font-extrabold max-custom-md:text-default-50'>
                {item.Title}
              </p>
              <p className='mt-1 text-xs text-default-500 font-extrabold max-custom-md:text-default-400'>{item.Summary}</p>
            </div>
            <ArrowRight className='size-4 translate-x-2.5 custom-md:hidden text-default-50' aria-hidden='true' />
          </a>
        ))}
      </>
      )
    : null
}

function renderImageTitleLinkImage (item) {
  const image = item.Media?.Image
  const imageByBreakpoint = image ? getImageByBreakpoint(image) : {}

  return image?.Url
    ? (
      <picture>
        {Object.keys(imageByBreakpoint).reverse().map((minWidth) => (
          <source key={minWidth} media={`(min-width: ${minWidth})`} srcSet={imageByBreakpoint[minWidth]} />
        ))}
        <img className='absolute object-cover w-full h-full inset-0' src={image.Url} alt={image.AltText || ''} />
      </picture>
      )
    : null
}

function renderImageTitleLinkColumn (column) {
  return column && column.length > 0
    ? (
      <div className={`p-4 grid max-w-1/3 gap-4 custom:md:gap-2 ${column.length === 1 || column.length === 2 ? 'max-custom-md:grid-cols-4 max-custom-md:grid-rows-2 ' : ''} ${column.length === 3 || column.length === 4 ? 'grid-cols-4 grid-rows-4 custom-md:min-w-[512px] custom-md:w-[512px] lg:min-w-[560px] lg:w-[560px]' : 'custom-md:min-w-[348px] custom-md:w-[396px] lg:min-w-[348px] lg:w-[396px]'}`}>
        {column.map((item, idx) => (
          <a
            key={idx}
            href={item.Link?.Url || '#'}
            target={item.Link?.Target || undefined}
            rel={item.Link?.Target === '_blank' ? 'noreferrer' : undefined}
            className={`
              relative max-custom-md:aspect-square overflow-hidden flex items-end p-4 rounded-xl before:content-[""] before:h-[66%] before:bg-[linear-gradient(180deg,_rgba(0,_0,_0,_0.00)_39.97%,_rgba(0,_0,_0,_0.90)_100.12%)]
              before:absolute before:left-0 before:right-0 before:bottom-0 before:z-10 before:backdrop-blur-[0.75px] before:opacity-80
              ${column.length === 1 ? 'w-full custom-md:h-full max-custom-md:row-span-2 max-custom-md:col-span-2' : ''}
              ${column.length === 2 ? 'w-full custom-md:h-full max-custom-md:row-span-2 max-custom-md:col-span-2' : ''}
              ${column.length === 3 && idx === 0 ? 'row-span-2 col-span-2 w-full custom-md:row-span-4 custom-md:col-span-2' : ''}
              ${column.length === 3 && (idx === 1 || idx === 2) ? 'row-span-2 col-span-2 custom-md:row-span-2 custom-md:col-span-2' : ''}
              ${column.length === 4 ? 'row-span-2 col-span-2' : ''}
            `}
          >
            {renderImageTitleLinkImage(item)}
            <p className='relative text-sm sm:text-base text-default-50 font-extrabold z-10'>{item.Title}</p>
          </a>
        ))}
      </div>
      )
    : null
}

function renderIconTitleSummaryColumn (title, column) {
  return column && column.length > 0
    ? (
      <>
        {title && <h3 className='uppercase text-[10px] tracking-[1.6px] text-default-500 custom-md:pt-1 pb-1 custom-md:pb-4'>{title}</h3>}
        {column.map((item, idx) => (
          <a
            key={idx}
            href={item.Link?.Url || '#'}
            target={item.Link?.Target || undefined}
            rel={item.Link?.Target === '_blank' ? 'noreferrer' : undefined}
            className='group flex items-center gap-6 relative rounded-lg py-2 text-sm/6 hover:bg-default-100 max-custom-md:hover:bg-transparent p-1 mb-4 last:mb-1'
          >
            <div className='flex size-11 items-center justify-center rounded-sm bg-primary-100 shrink-0'>
              <img src={item.Media.Image.Url} alt={item.Media.Image.AltText || ''} className='size-6' />
            </div>
            <div>
              <p className='text-base leading-[1.2] text-default-900 font-extrabold max-custom-md:text-default-50'>
                {item.Title}
              </p>
              <p className='mt-1 text-xs text-default-500 font-extrabold max-custom-md:text-default-400'>{item.Summary}</p>
            </div>
          </a>
        ))}
      </>
      )
    : null
}

function renderSubNavigationGroup (group) {
  // Render based on group.Type
  switch (group.Type) {
    case '1ColumnTitleSummary':
      return (
        <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3'>
          {renderTitleSummaryColumn(group.Title, group.TitleSummaryColumn1)}
        </div>
      )
    case '2ColumnsTitleSummary':
      return (
        <>
          <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3 max-custom-md:pb-0'>
            {renderTitleSummaryColumn(group.Title, group.TitleSummaryColumn1)}
          </div>
          <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3 max-custom-md:pt-1'>
            {renderTitleSummaryColumn(group.Title, group.TitleSummaryColumn2)}
          </div>
        </>
      )
    case '1ColumnImagesTitle':
      return (
        <>
          {renderImageTitleLinkColumn(group.ImageTitleLinkColumn1)}
        </>
      )
    case '1ColumnIconTitleSummary':
      return (
        <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3'>
          {renderIconTitleSummaryColumn(group.Title, group.IconTitleSummaryColumn1)}
        </div>
      )
    case '2ColumnsIconTitleSummary':
      return (
        <>
          <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3'>
            {renderIconTitleSummaryColumn(group.Title, group.IconTitleSummaryColumn1)}
          </div>
          <div className='py-3 px-6 custom-md:py-4 custom-md:px-4 w-full custom-md:w-[320px] max-w-1/3'>
            {renderIconTitleSummaryColumn(group.Title, group.IconTitleSummaryColumn2)}
          </div>
        </>
      )
    default:
      return null
  }
}

const SearchForm = () => {
  const [value, setValue] = useState('')
  const handleSubmit = (e) => {
    e.preventDefault()
    if (value.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(value.trim())}`
    }
  }
  return (
    <form className='relative w-full' onSubmit={handleSubmit}>
      <input
        type='search'
        className='outline-none text-base py-2 pr-4 pl-10 rounded-md placeholder:text-default-400 w-full border border-default-200'
        placeholder='How can we help?'
        value={value}
        onChange={e => setValue(e.target.value)}
      />
      <button className='absolute top-1/2 -translate-y-1/2 left-0 outline-none inline-flex items-center justify-center size-[36px] rounded-full hover:bg-primary-100 active:bg-primary-100' type='submit'>
        <span className='sr-only'>Search [input value]</span>
        <Search className='size-4' />
      </button>
    </form>
  )
}

const MobileSecondaryMenu = () => (
  <>
    <Disclosure as='div' className='-mx-3'>
      <DisclosureButton className='group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-sm font-semibold text-default-50'>
        Login
        <ChevronDownIcon aria-hidden='true' className='size-5 flex-none group-data-[open]:rotate-180' />
      </DisclosureButton>
      <DisclosurePanel className='mt-2 space-y-2'>
        <a href='https://nz.udclive.co.nz/external/login' target='_blank' rel='noreferrer' className='block px-3 py-2 text-sm text-default-50 font-semibold'>UDC Live</a>
        <a href='https://my.udc.co.nz/logon' target='_blank' rel='noreferrer' className='block px-3 py-2 text-sm text-default-50 font-semibold'>myUDC</a>
      </DisclosurePanel>
    </Disclosure>
    <a href='/help-resources/contact-us/' size='sm' variant='primary' className='w-full text-sm text-default-50 grow text-align-left font-semibold mb-2'>Get in touch</a>
    <Button
      href='#apply-dialog'
      variant='primary-alt'
      size='sm'
      mobile='block'
      componentName='Navigation'
    >
      Apply now
    </Button>
  </>
)

const navigation = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'light'
  umbraco = umbraco.Navigation

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isPopoverSearchOpen, setIsPopoverSearchOpen] = useState(false)
  const [popoverSecondaryOpen, setPopoverSecondaryOpen] = useState(false)
  const [popoverSecondarySmallOpen, setPopoverSecondarySmallOpen] = useState(false)

  const [popoversOpen, setPopoversOpen] = useState(Array(umbraco.length).fill(false))

  // Create refs for each popover button for hover functionality
  const popoverButtonRefs = useRef({})

  const handlePopoverOpen = (idx, open) => {
    setPopoversOpen(prev => {
      if (prev[idx] === open) return prev
      const next = [...prev]
      next[idx] = open
      return next
    })
  }

  // Check if we're on desktop (custom-md breakpoint)
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const checkDesktop = () => {
      // custom-md breakpoint is typically 768px, adjust if needed
      setIsDesktop(window.innerWidth >= 768)
    }

    checkDesktop()
    window.addEventListener('resize', checkDesktop)
    return () => window.removeEventListener('resize', checkDesktop)
  }, [])

  const anyPopoverOpen = popoversOpen.some(Boolean)

  const [scrolled, setScrolled] = useState(false)
  const [scrollDirection, setScrollDirection] = useState('up')
  const [scrolledPosition, setScrolledPosition] = useState(0)
  const lastScrollY = useRef(0)

  useEffect(() => {
    const container = document.getElementById('page-content')
    let scrollTimeout

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isScrollingDown = currentScrollY > lastScrollY.current

      // Reference: are we at the top of #page-content?
      let atTop = false
      if (container) {
        // Get the top position of #page-content relative to viewport
        const rect = container.getBoundingClientRect()
        atTop = rect.top >= 0
      }

      setScrolled(!atTop && currentScrollY > 0)

      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down')
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up')
      }

      // Handle scrolledPosition with delay for downward scrolling
      if (isScrollingDown) {
        // Clear any existing timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout)
        }
        // Delay setScrolledPosition by 1000ms when scrolling down
        scrollTimeout = setTimeout(() => {
          setScrolledPosition(container?.getBoundingClientRect().top || 0)
        }, 1000)
      } else {
        // Clear timeout and update immediately when scrolling up
        if (scrollTimeout) {
          clearTimeout(scrollTimeout)
        }
        setScrolledPosition(container?.getBoundingClientRect().top || 0)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [])

  return (
    <div
      data-component='navigation'
      data-theme={theme}
      className={`_site-header ${scrolledPosition > -200 ? '' : '_scrolled-header-styles'} bg-[--header-bg] isolate z-50 w-full transition duration-500 top-0 ${scrolled ? 'fixed' : 'absolute'} ${scrolled ? (scrollDirection === 'down' ? '-translate-y-[88px]' : '-translate-y-0') : 'translate-y-0'}`}
      onPointerUp={(e) => {
        if (e.target.closest('a')) {
          gtmTrackEvent('clickNavigationLink', { link: e.target.closest('a').href, pageURL: window.location.href })
        }
        if (e.target.closest('button')) {
          gtmTrackEvent('clickNavigationButton', { button: e.target.closest('button').innerText, pageURL: window.location.href })
        }
      }}
    >
      <nav aria-label='Global' className='mx-auto flex max-w-7xl items-center justify-between py-4 px-4 md:px-6 lg:px-8'>
        <div className='flex text-white mr-2 custom-lg:mr-10'>
          <a href='/' className='-m-1.5 p-1.5'>
            <span className='sr-only'>UDC</span>
            <BrandLogo className='w-[86px] lg:w-auto h-auto' />
          </a>
        </div>
        {/* Popover group and search button */}
        <div className='flex items-center gap-4 grow max-custom-lg:justify-end z-10 max-custom-lg:pr-4'>

          <PopoverGroup className='hidden custom-md:flex items-center gap-0.5 h-[54px] bg-default-50 rounded-full shadow-md px-2 text-[--theme-nav-pill-text]'>
            {umbraco.map((navSection, idx) => {
              // Get or create ref for this popover button
              if (!popoverButtonRefs.current[idx]) {
                popoverButtonRefs.current[idx] = React.createRef()
              }
              const buttonRef = popoverButtonRefs.current[idx]

              return (
                <HoverablePopover
                  key={navSection.Title}
                  navSection={navSection}
                  idx={idx}
                  isOpen={popoversOpen[idx]}
                  onOpenChange={handlePopoverOpen}
                  isDesktop={isDesktop}
                  buttonRef={buttonRef}
                />
              )
            })}
          </PopoverGroup>

          <div id='search-popover' className='relative max-custom-md:hidden shrink-0 flex items-center justify-center bg-default-50 size-[54px] rounded-full shadow-md'>
            <Popover>
              {({ open }) => {
                if (open !== isPopoverSearchOpen) setTimeout(() => setIsPopoverSearchOpen(open), 0)
                open && setTimeout(() => {
                  document.querySelector('#search-popover input')?.focus()
                }, 0)

                return (
                  <>
                    <PopoverButton className={`outline-none inline-flex items-center justify-center size-[44px] rounded-full hover:bg-primary-100 active:bg-primary-100 ${open ? 'bg-primary-100' : ''}`}>
                      <span className='sr-only'>Search</span>
                      <Search />
                    </PopoverButton>
                    <PopoverPanel
                      transition
                      className='absolute p-8 w-[540px] right-0 top-[65px] bg-default-50 rounded-[20px] shadow-md transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
                    >
                      <div className='flex flex-start'>
                        <SearchForm />
                      </div>
                    </PopoverPanel>
                  </>
                )
              }}
            </Popover>
          </div>
          <div className={`fixed -z-10 w-[100dvw] h-[100dvh] inset-0 ease-in transition-opacity backdrop-blur-[2.5px] ${anyPopoverOpen || isPopoverSearchOpen ? 'opacity-100 pointer-events-auto duration-200' : 'opacity-0 pointer-events-none duration-400'}`} />
        </div>
        <div className={`fixed -z-10 w-[100dvw] h-[100dvh] inset-0 transition-opacity ${popoverSecondaryOpen || popoverSecondarySmallOpen ? 'opacity-100 pointer-events-auto duration-500' : 'opacity-0 pointer-events-none duration-1000'}`} />

        <div className='flex gap-2 w-[54px] custom-lg:w-[310px] lg:justify-end relative'>
          <div className='flex custom-md:hidden'>
            <button
              type='button'
              onClick={() => setMobileMenuOpen(true)}
              className='inline-flex items-center justify-center bg-default-50 size-[54px] rounded-full shadow-md hover:bg-primary-100 active:bg-primary-100'
            >
              <span className='sr-only'>Open main menu</span>
              <Menu />
            </button>
          </div>
          <div className='hidden custom-md:flex custom-lg:hidden'>
            <Popover>
              {({ open }) => {
                if (open !== popoverSecondarySmallOpen) setTimeout(() => setPopoverSecondarySmallOpen(open), 0)
                return (
                  <>
                    <PopoverButton className={`outline-none inline-flex items-center justify-center bg-default-50 size-[54px] rounded-full shadow-md hover:bg-primary-100 active:bg-primary-100 ${open ? 'bg-primary-100' : ''}`}>
                      {open && (
                        <>
                          <span className='sr-only'>Close menu</span>
                          <Delete className='size-5' />
                        </>
                      )}
                      {!open && (
                        <>
                          <span className='sr-only'>Open menu</span>
                          <Menu className='size-5' />
                        </>
                      )}
                    </PopoverButton>
                    <PopoverPanel
                      transition
                      className='absolute p-5 w-[360px] right-0 top-[64px] bg-dark-blue-nav rounded-[20px] shadow-md transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
                    >
                      <div className='flex flex-col gap-4'>
                        <MobileSecondaryMenu />
                      </div>
                    </PopoverPanel>
                  </>
                )
              }}
            </Popover>
          </div>
          <div className='hidden custom-lg:flex gap-2'>
            <Popover>
              {({ open }) => {
                if (open !== popoverSecondaryOpen) setTimeout(() => setPopoverSecondaryOpen(open), 0)
                return (
                  <>
                    <PopoverButton className='text-[--theme-nav-text] pl-2 pr-1 py-[9px] outline-none flex items-center gap-x-1 text-sm font-semibold rounded-full'>
                      Login
                      <Chevron className={`flex-none translate-y-0.5 ${open ? 'rotate-180' : ''}`} />
                    </PopoverButton>
                    <PopoverPanel
                      transition
                      className='absolute p-5 w-[196px] left-2 top-[64px] bg-default-50 rounded-[20px] shadow-md transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in'
                    >
                      <div className='flex flex-col gap-2'>
                        <Button href='https://nz.udclive.co.nz/external/login' target='_blank' size='sm' variant='secondary' className='w-full'>
                          <span className='block relative pr-5'>UDC Live <ArrowRightCircle className='absolute right-0 top-1/2 -mt-[7px]' /></span>
                        </Button>

                        <Button href='https://my.udc.co.nz/logon' target='_blank' size='sm' variant='primary' className='w-full'>
                          <span className='block relative pr-5'>myUDC <ArrowRightCircle className='absolute right-0 top-1/2 -mt-[7px]' /></span>
                        </Button>
                      </div>
                    </PopoverPanel>
                  </>
                )
              }}
            </Popover>
            <a className='text-[--theme-nav-text] pl-1 pr-2 py-[9px] outline-none flex items-center gap-x-1 text-sm font-semibold rounded-full' href='/help-resources/contact-us/'>Get in touch</a>
            <Button
              href='#apply-dialog'
              variant='primary-alt'
              size='sm'
              componentName='Navigation'
            >
              Apply now
            </Button>
          </div>
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className='lg:hidden'>
        <div className='fixed inset-0 z-10' />
        <DialogPanel className={`fixed inset-0 z-50 w-full overflow-y-auto bg-dark-blue-nav py-4 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000`}>
          <div className='flex items-center justify-between h-[86px] bg-dark-blue-nav fixed top-0 left-0 right-0 z-20 px-4 py-6'>
            <div data-theme='dark' className='_site-header flex text-white mr-2 custom-lg:mr-10'>
              <a href='/' className='-m-1.5 p-1.5'>
                <span className='sr-only'>UDC</span>
                <BrandLogo className='w-[86px] lg:w-auto h-auto' />
              </a>
            </div>
            <button
              type='button'
              onClick={() => setMobileMenuOpen(false)}
              className='inline-flex items-center justify-center bg-default-50 size-[54px] rounded-full shadow-md hover:bg-primary-100 active:bg-primary-100'
            >
              <span className='sr-only'>Close menu</span>
              <Delete />
            </button>
          </div>
          <div className='mt-6 flow-root pt-16'>
            <div className='px-4 pb-2'>
              <SearchForm />
            </div>
            <div className='pt-3'>
              <div className='px-4 space-y-2 py-4'>
                {umbraco.map((navSection, idx) => (
                  <Disclosure as='div' className='-mx-3' key={navSection.Title}>
                    <DisclosureButton className='group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-default-50'>
                      {navSection.Title}
                      <ChevronDownIcon aria-hidden='true' className='size-5 flex-none group-data-[open]:rotate-180' />
                    </DisclosureButton>
                    <DisclosurePanel className='mt-2 space-y-2'>
                      {navSection.Link?.Url && (
                        <a
                          href={navSection.Link.Url}
                          target={navSection.Link.Target === '_blank' ? '_blank' : undefined}
                          rel={navSection.Link.Target === '_blank' ? 'noreferrer' : undefined}
                          className='inline-flex items-center gap-x-1.5 px-3 py-2 -mt-2 text-sm text-default-50 opacity-90 font-semibold mb-2 hover:underline relative'
                        >
                          View all
                          <ArrowRightCircle className='size-4 -mb-1' />
                        </a>
                      )}
                      {navSection.SubNavigationGroup.map((group, gidx) => (
                        <div key={gidx}>{renderSubNavigationGroup(group)}</div>
                      ))}
                    </DisclosurePanel>
                  </Disclosure>
                ))}
              </div>
              <div className='px-4 py-4 border-t border-default-800'>
                <div className='flex flex-col gap-4'>
                  <MobileSecondaryMenu />
                </div>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </div>
  )
}

reactMount('GlobalNavigation', navigation)
